/**
 * 비밀번호 강도 검증 유틸리티 (다국어 지원)
 */

type SupportedLanguage = 'ko' | 'en' | 'es';

interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
  score: number; // 0-100
}

const PASSWORD_MESSAGES = {
  ko: {
    min_length: '비밀번호는 최소 8자 이상이어야 합니다.',
    uppercase: '비밀번호에 대문자가 포함되어야 합니다.',
    lowercase: '비밀번호에 소문자가 포함되어야 합니다.',
    digit: '비밀번호에 숫자가 포함되어야 합니다.',
    special_char: '비밀번호에 특수문자가 포함되어야 합니다.',
    consecutive: '동일한 문자가 3번 이상 연속으로 사용될 수 없습니다.',
    common_pattern: '너무 간단한 비밀번호는 사용할 수 없습니다.',
  },
  en: {
    min_length: 'Password must be at least 8 characters.',
    uppercase: 'Password must contain an uppercase letter.',
    lowercase: 'Password must contain a lowercase letter.',
    digit: 'Password must contain a number.',
    special_char: 'Password must contain a special character.',
    consecutive: 'Password cannot have the same character repeated 3 or more times.',
    common_pattern: 'Password is too simple.',
  },
  es: {
    min_length: 'La contraseña debe tener al menos 8 caracteres.',
    uppercase: 'La contraseña debe contener una letra mayúscula.',
    lowercase: 'La contraseña debe contener una letra minúscula.',
    digit: 'La contraseña debe contener un número.',
    special_char: 'La contraseña debe contener un carácter especial.',
    consecutive: 'No se puede repetir el mismo carácter 3 o más veces seguidas.',
    common_pattern: 'La contraseña es demasiado simple.',
  },
} as const;

type MessageKey = keyof (typeof PASSWORD_MESSAGES)['ko'];

function getMessage(key: MessageKey, language: SupportedLanguage): string {
  try {
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { i18n: { t: (k: string, opts: { lng: string }) => string } }).i18n
    ) {
      return (
        window as unknown as { i18n: { t: (k: string, opts: { lng: string }) => string } }
      ).i18n.t(`password_validation.${key}`, { lng: language });
    }
  } catch {
    // fallback to static messages
  }
  return PASSWORD_MESSAGES[language]?.[key] ?? PASSWORD_MESSAGES.en[key];
}

function detectLanguage(): SupportedLanguage {
  try {
    const stored = typeof localStorage !== 'undefined' && localStorage.getItem('i18nextLng');
    if (stored && stored in PASSWORD_MESSAGES) return stored as SupportedLanguage;
  } catch {
    // ignore
  }
  return 'ko';
}

/**
 * 비밀번호 강도 검증
 * @param password 검증할 비밀번호
 * @param language 언어 코드 (미지정 시 자동 감지)
 */
export function validatePasswordStrength(
  password: string,
  language?: SupportedLanguage
): PasswordValidationResult {
  const lang = language ?? detectLanguage();
  const errors: string[] = [];
  let score = 0;

  if (password.length < 8) {
    errors.push(getMessage('min_length', lang));
  } else {
    score += 20;
  }

  if (!/[A-Z]/.test(password)) {
    errors.push(getMessage('uppercase', lang));
  } else {
    score += 20;
  }

  if (!/[a-z]/.test(password)) {
    errors.push(getMessage('lowercase', lang));
  } else {
    score += 20;
  }

  if (!/\d/.test(password)) {
    errors.push(getMessage('digit', lang));
  } else {
    score += 20;
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push(getMessage('special_char', lang));
  } else {
    score += 20;
  }

  if (/(.)\1{2,}/.test(password)) {
    errors.push(getMessage('consecutive', lang));
  }

  const commonPatterns = [/123456/, /password/i, /qwerty/i, /admin/i, /user/i, /abc123/i];

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      errors.push(getMessage('common_pattern', lang));
      break;
    }
  }

  let strength: 'weak' | 'medium' | 'strong';
  if (score < 60) {
    strength = 'weak';
  } else if (score < 80) {
    strength = 'medium';
  } else {
    strength = 'strong';
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
    score,
  };
}
